import React from "react";
//#region redux
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "store";
import { preliminaryTaxUpdateSubmitted } from "store/actions/preliminaryTaxUpdateActions";
//#endregion
//#region types
import { TodoCardProps } from "models/propTypes/todoPropTypes";
import { StyleEnums, CurrencyEnums, TodoEnums } from "models/enums";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import TopTodoCard from "../../_partials/topTodoCard";
import { TitledContainerInner, EntityCardContent, PageFooter, SimpleAccordionTable } from "components/common";
import PreliminaryTaxUpdateFormContainer from "./preliminaryTaxUpdateFormContainer";
import { Grid, Typography } from "@mui/material";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import { usePreliminaryTaxUpdateService } from "serviceHooks";
import cssExports from "theme/styles/main.module.scss";

const PreliminaryTaxUpdateTodo: React.FC<TodoCardProps> = (props) => {
  const preliminaryTaxUpdateService = usePreliminaryTaxUpdateService();
  const dispatch = useDispatch();
  const companyName = useSelector((store: AppState) => store.auth.selectedCompany.name ?? "");
  const preliminaryTaxUpdate = props.item.preliminaryTaxUpdate;
  const [selectedResponse, setSelectedResponse] = React.useState<TodoEnums.PreliminaryTaxUpdateResponse | undefined>(
    undefined
  );

  const onSubmit = () => {
    if (selectedResponse === TodoEnums.PreliminaryTaxUpdateResponse.Accept) {
      return preliminaryTaxUpdateService.accept(props.item.id);
    }
    if (selectedResponse === TodoEnums.PreliminaryTaxUpdateResponse.Decline) {
      return preliminaryTaxUpdateService.decline(props.item.id);
    }
    return Promise.resolve();
  };

  const formattedAmount = preliminaryTaxUpdate
    ? LocalizationHelperInstance.getFormattedCurrency(
        preliminaryTaxUpdate.finalTaxToPay,
        CurrencyEnums.CurrencyCode.SEK
      )
    : "";

  const financialYear = preliminaryTaxUpdate
    ? DateHelper.formatFinancialYear(
        preliminaryTaxUpdate.financialYearStart,
        preliminaryTaxUpdate.financialYearEnd,
        "-"
      ).replace(/-/g, "\u2011")
    : "";

  return (
    <EntityCardContent
      topTodoCard={
        <TopTodoCard
          item={props.item}
          noDescriptionTextWrap
          descriptionHeadingOne={`${LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Common.FinancialYear2
          )} ${financialYear}`}
          descriptionHeadingTwo={companyName}
          footerText={`${LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Common.Latest
          )}: ${DateHelper.formatDate(props.item.date)}`}
        />
      }
      todoMainSection={
        <TitledContainerInner
          header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Todos.PreliminaryTaxUpdate_Header)}
          subHeader={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Todos.PreliminaryTaxUpdate_Body)}
        >
          <Grid item container rowGap={2} marginTop={1.5}>
            <Grid item>
              <Typography className={cssExports.bodyText}>
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Todos.PreliminaryTaxUpdate_Body2)}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                [`& .${cssExports.simpleAccordionTableRow}.${cssExports.expandable}:hover`]: { cursor: "default" },
                [`& .${cssExports.simpleAccordionTableRow} .${cssExports.titleCell}`]: { width: "45%" },
                [`& .${cssExports.simpleAccordionTableRow} .${cssExports.valueCell}`]: { textAlign: "left" },
              }}
            >
              <SimpleAccordionTable
                items={[
                  {
                    title: LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.Todos.PreliminaryTaxUpdate_TaxToPay
                    ),
                    value: formattedAmount,
                  },
                  {
                    title: LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.Todos.PreliminaryTaxUpdate_InterestFrom
                    ),
                    value: preliminaryTaxUpdate?.interestCalculationStartDate
                      ? DateHelper.formatDate(preliminaryTaxUpdate.interestCalculationStartDate)
                      : "",
                  },
                ]}
              />
            </Grid>
          </Grid>
          <PreliminaryTaxUpdateFormContainer
            preliminaryTaxUpdate={preliminaryTaxUpdate}
            selectedResponse={selectedResponse}
            onResponseChange={setSelectedResponse}
            questionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Todos.PreliminaryTaxUpdate_Question
            )}
          />
        </TitledContainerInner>
      }
      footer={
        <PageFooter
          actions={[
            {
              text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Todos.PreliminaryTaxUpdate_Submit),
              onClick: onSubmit,
              type: StyleEnums.DisplayType.Success,
              onActionSuccess: () => {
                dispatch(preliminaryTaxUpdateSubmitted(props.item.id));
                props.onActionSuccess?.();
              },
              isDisabled: selectedResponse === undefined,
            },
          ]}
        />
      }
    />
  );
};

export default PreliminaryTaxUpdateTodo;
